import { useState, useEffect, useCallback } from "react";

// ── Types ────────────────────────────────────────────────────────────────────

interface AdminUser {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
  tenantId: string;
  workspaceId: string;
  createdAt: string;
  enabled: boolean;
  lastActivityAt: string | null;
  oauthProvider?: string | null;
}

interface CreateUserForm {
  email: string;
  displayName: string;
  password: string;
  roles: string[];
}

interface PaginatedUsers {
  users: AdminUser[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

// ── API helpers ──────────────────────────────────────────────────────────────

const BASE = "/api";

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem("flax_token") ?? sessionStorage.getItem("flax_token");
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

async function fetchUsers(page: number, limit: number): Promise<PaginatedUsers> {
  const res = await fetch(`${BASE}/admin/users?page=${page}&limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error(`Failed to fetch users: ${res.status}`);
  return res.json();
}

async function createUser(data: CreateUserForm): Promise<AdminUser> {
  const res = await fetch(`${BASE}/admin/users`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Failed to create user: ${res.status}`);
  }
  const result = await res.json();
  return result.user;
}

async function updateUser(
  id: string,
  data: { displayName?: string; roles?: string[]; enabled?: boolean },
): Promise<AdminUser> {
  const res = await fetch(`${BASE}/admin/users/${id}`, {
    method: "PATCH",
    headers: getAuthHeaders(),
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message ?? `Failed to update user: ${res.status}`);
  }
  const result = await res.json();
  return result.user;
}

// ── Available roles ──────────────────────────────────────────────────────────

const AVAILABLE_ROLES = ["admin", "operator", "viewer", "user"];

// ── Component ────────────────────────────────────────────────────────────────

export function UserManager() {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create user modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserForm>({
    email: "",
    displayName: "",
    password: "",
    roles: ["user"],
  });
  const [createError, setCreateError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Edit user
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editRoles, setEditRoles] = useState<string[]>([]);

  const loadUsers = useCallback(async () => {
    try {
      setError(null);
      const data = await fetchUsers(page, limit);
      setUsers(data.users);
      setTotal(data.total);
      setHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleCreateUser = async () => {
    setCreateError(null);
    setCreating(true);
    try {
      await createUser(createForm);
      setShowCreateModal(false);
      setCreateForm({ email: "", displayName: "", password: "", roles: ["user"] });
      await loadUsers();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateRoles = async (userId: string, roles: string[]) => {
    try {
      const updated = await updateUser(userId, { roles });
      setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
      setEditingUserId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update roles");
    }
  };

  const handleToggleEnabled = async (user: AdminUser) => {
    try {
      const newEnabled = !user.enabled;
      // Toggle enable by adding/removing all roles
      const roles = newEnabled ? ["user"] : [];
      const updated = await updateUser(user.id, { roles, enabled: newEnabled });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? updated : u)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to toggle user");
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center" data-testid="users-loading">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-ink-300 border-t-accent-600" />
          <p className="mt-3 text-sm text-ink-500">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6" data-testid="user-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">User Management</h1>
          <p className="text-sm text-ink-500">{total} total users</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="rounded-lg bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700"
          data-testid="create-user-btn"
        >
          Create User
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* User Table */}
      <div className="overflow-hidden rounded-lg border border-ink-100 bg-white" data-testid="user-table">
        <table className="w-full text-sm">
          <thead className="border-b border-ink-100 bg-surface-2">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                Roles
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                Created
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                Last Activity
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-ink-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-50">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-surface-2" data-testid="user-row">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-100 text-xs font-semibold text-accent-700">
                      {user.displayName.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-medium text-ink-800">{user.displayName}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-ink-600">{user.email}</td>
                <td className="px-4 py-3">
                  {editingUserId === user.id ? (
                    <div className="flex items-center gap-2">
                      <select
                        multiple
                        value={editRoles}
                        onChange={(e) => {
                          const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                          setEditRoles(selected);
                        }}
                        className="rounded border border-ink-200 px-2 py-1 text-xs"
                        data-testid="role-select"
                      >
                        {AVAILABLE_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => handleUpdateRoles(user.id, editRoles)}
                        className="rounded bg-emerald-600 px-2 py-1 text-xs text-white hover:bg-emerald-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingUserId(null)}
                        className="rounded bg-ink-200 px-2 py-1 text-xs text-ink-700 hover:bg-ink-300"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {user.roles.map((role) => (
                        <span
                          key={role}
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                            role === "admin"
                              ? "bg-red-100 text-red-700"
                              : role === "operator"
                                ? "bg-amber-100 text-amber-700"
                                : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {role}
                        </span>
                      ))}
                      <button
                        onClick={() => {
                          setEditingUserId(user.id);
                          setEditRoles(user.roles);
                        }}
                        className="text-xs text-accent-600 hover:text-accent-700"
                        data-testid="edit-roles-btn"
                      >
                        edit
                      </button>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-ink-500">
                  {new Date(user.createdAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-xs text-ink-500">
                  {user.lastActivityAt
                    ? new Date(user.lastActivityAt).toLocaleDateString()
                    : "Never"}
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleToggleEnabled(user)}
                    className={`rounded-full px-3 py-1 text-xs font-medium ${
                      user.enabled
                        ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                        : "bg-red-100 text-red-700 hover:bg-red-200"
                    }`}
                    data-testid="toggle-enabled-btn"
                  >
                    {user.enabled ? "Enabled" : "Disabled"}
                  </button>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-ink-400">
                  No users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between" data-testid="pagination">
        <p className="text-sm text-ink-500">
          Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
        </p>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-lg border border-ink-200 px-3 py-1.5 text-sm text-ink-600 hover:bg-surface-2 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="flex items-center px-2 text-sm text-ink-600">
            Page {page} of {Math.max(1, Math.ceil(total / limit))}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={!hasMore}
            className="rounded-lg border border-ink-200 px-3 py-1.5 text-sm text-ink-600 hover:bg-surface-2 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="create-user-modal">
          <div className="w-full max-w-md rounded-xl border border-ink-100 bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-ink-900">Create New User</h2>

            {createError && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-2 text-sm text-red-700">
                {createError}
              </div>
            )}

            <div className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-700">Email</label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
                  placeholder="user@example.com"
                  data-testid="create-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">Display Name</label>
                <input
                  type="text"
                  value={createForm.displayName}
                  onChange={(e) => setCreateForm((f) => ({ ...f, displayName: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
                  placeholder="Jane Doe"
                  data-testid="create-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">Password</label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                  className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
                  placeholder="Min 8 characters"
                  data-testid="create-password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700">Roles</label>
                <select
                  multiple
                  value={createForm.roles}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                    setCreateForm((f) => ({ ...f, roles: selected }));
                  }}
                  className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
                  data-testid="create-roles"
                >
                  {AVAILABLE_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setCreateError(null);
                }}
                className="rounded-lg border border-ink-200 px-4 py-2 text-sm text-ink-600 hover:bg-surface-2"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                disabled={creating || !createForm.email || !createForm.displayName || !createForm.password}
                className="rounded-lg bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
                data-testid="create-submit"
              >
                {creating ? "Creating..." : "Create User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
